import { ACCESS_TYPES, resolveOrgAccess } from 'packages/utils/access';

import {
  buildCompleteRegistrationPath,
  getCompleteRegistrationOrgId,
  isCompleteRegistrationPathname,
} from '@src/packages/users/utils/completeRegistrationPath';

/**
 * Invitees (any role but Owner) stay pending until the backend accepts their onboarding
 * profile. However they signed in (invite link, Google, password reset, email link), they
 * finish it on /complete-registration. Self sign-up owners use the /onboarding wizard.
 *
 * Pending is read from the active organization's user doc, which assignment uses, and from
 * the root profile flag.
 */
export const mustCompleteRegistration = ({ claims, orgId, orgUser, onboardingCompleted }) => {
  const orgAccess = resolveOrgAccess(claims, orgId, orgUser?.access);
  if (!orgAccess || orgAccess === ACCESS_TYPES.OWNER) {
    return false;
  }
  return onboardingCompleted === false || orgUser?.onboardingCompleted === false;
};

/**
 * Registration step of the app guard, run once the profile is loaded. Returns `null` when the
 * rest of the guard decides; otherwise `{ to }` to navigate, or `{}` to stay put.
 *
 * Pending is read from the active organization's user doc, which is refetched on every
 * organization select. While it loads, the guard must not decide from the root flag alone.
 */
export const resolveRegistrationRedirect = ({
  shouldCompleteRegistration,
  onboardingCompleted,
  userHasOrganizations,
  orgRolePending,
  activeOrgId,
  orgUserLoaded,
  pathname,
  search,
  isInvitationPath,
}) => {
  if (isInvitationPath) {
    return null;
  }
  const onCompleteRegistration = isCompleteRegistrationPathname(pathname);
  const orgUserLoading = orgRolePending || (Boolean(activeOrgId) && orgUserLoaded === false);

  if (shouldCompleteRegistration) {
    const onTargetOrg =
      onCompleteRegistration && getCompleteRegistrationOrgId(search) === (activeOrgId || undefined);
    return onTargetOrg ? {} : { to: buildCompleteRegistrationPath(activeOrgId) };
  }
  // Wait for the org user doc before choosing between the invitee form, the wizard or home.
  if (orgUserLoading && (onCompleteRegistration || (onboardingCompleted === false && userHasOrganizations))) {
    return {};
  }
  if (onboardingCompleted && onCompleteRegistration) {
    // An existing account accepting another organization's invite: that organization is
    // pending and hidden from the switcher, so the page asks the backend for it.
    const requestedOrgId = getCompleteRegistrationOrgId(search);
    if (requestedOrgId && requestedOrgId !== activeOrgId) {
      return {};
    }
    return { to: '/' };
  }
  return null;
};
